import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit2,
  Percent,
  Tag,
  Clock,
  Calendar,
  Save,
  Loader2,
  Gift,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
];

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Porcentaje (%)', icon: Percent },
  { value: 'fixed', label: 'Monto fijo ($)', icon: Tag },
  { value: '2x1', label: '2x1', icon: Gift },
  { value: '3x2', label: '3x2', icon: Zap },
];

export default function Promotions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [promotions, setPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    days_of_week: [],
    start_time: '',
    end_time: '',
    category_ids: [],
    product_ids: [],
    is_active: true,
    priority: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [promosRes, menuRes] = await Promise.all([
        axios.get(`${API}/promotions`),
        axios.get(`${API}/menu`)
      ]);
      setPromotions(promosRes.data);
      setCategories(menuRes.data.categories || []);
      setProducts(menuRes.data.products || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      days_of_week: [],
      start_time: '',
      end_time: '',
      category_ids: [],
      product_ids: [],
      is_active: true,
      priority: 0
    });
    setEditingPromo(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (promo) => {
    setEditingPromo(promo);
    setForm({
      name: promo.name,
      description: promo.description || '',
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      days_of_week: promo.conditions?.days_of_week || [],
      start_time: promo.conditions?.start_time || '',
      end_time: promo.conditions?.end_time || '',
      category_ids: promo.conditions?.category_ids || [],
      product_ids: promo.conditions?.product_ids || [],
      is_active: promo.is_active,
      priority: promo.priority
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error('Ingresa un nombre para la promoción');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        discount_type: form.discount_type,
        discount_value: form.discount_value,
        conditions: {
          days_of_week: form.days_of_week.length > 0 ? form.days_of_week : null,
          start_time: form.start_time || null,
          end_time: form.end_time || null,
          category_ids: form.category_ids.length > 0 ? form.category_ids : null,
          product_ids: form.product_ids.length > 0 ? form.product_ids : null
        },
        is_active: form.is_active,
        priority: form.priority
      };

      if (editingPromo) {
        await axios.put(`${API}/promotions/${editingPromo.promotion_id}`, payload);
        toast.success('Promoción actualizada');
      } else {
        await axios.post(`${API}/promotions`, payload);
        toast.success('Promoción creada');
      }
      
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar promoción');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('¿Eliminar esta promoción?')) return;
    
    try {
      await axios.delete(`${API}/promotions/${promoId}`);
      toast.success('Promoción eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/promotions/${promo.promotion_id}`, {
        is_active: !promo.is_active
      });
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const toggleDay = (day) => {
    if (form.days_of_week.includes(day)) {
      setForm({ ...form, days_of_week: form.days_of_week.filter(d => d !== day) });
    } else {
      setForm({ ...form, days_of_week: [...form.days_of_week, day] });
    }
  };

  const getDiscountLabel = (promo) => {
    switch (promo.discount_type) {
      case 'percentage': return `${promo.discount_value}% OFF`;
      case 'fixed': return `$${promo.discount_value} OFF`;
      case '2x1': return '2x1';
      case '3x2': return '3x2';
      default: return promo.discount_type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold">Promociones y Descuentos</h1>
              <p className="text-sm text-muted-foreground">Configura ofertas automáticas</p>
            </div>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Promoción
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="p-6">
        {promotions.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Gift className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Sin promociones</h3>
              <p className="text-muted-foreground mb-4">Crea tu primera promoción para atraer más clientes</p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Crear Promoción
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {promotions.map((promo) => (
              <Card key={promo.promotion_id} className={!promo.is_active ? 'opacity-60' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{promo.name}</CardTitle>
                      {promo.description && (
                        <CardDescription>{promo.description}</CardDescription>
                      )}
                    </div>
                    <Badge className={promo.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {getDiscountLabel(promo)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Conditions */}
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    {promo.conditions?.days_of_week?.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {promo.conditions.days_of_week.map(d => DAYS_OF_WEEK[d]?.label?.slice(0,3)).join(', ')}
                        </span>
                      </div>
                    )}
                    {promo.conditions?.start_time && promo.conditions?.end_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{promo.conditions.start_time} - {promo.conditions.end_time}</span>
                      </div>
                    )}
                    {promo.conditions?.category_ids?.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        <span>
                          {promo.conditions.category_ids.map(id => 
                            categories.find(c => c.category_id === id)?.name
                          ).filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    {!promo.conditions?.days_of_week?.length && 
                     !promo.conditions?.start_time && 
                     !promo.conditions?.category_ids?.length && (
                      <span className="text-amber-500">Aplica siempre a todo</span>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={promo.is_active} 
                        onCheckedChange={() => toggleActive(promo)}
                      />
                      <span className="text-sm">{promo.is_active ? 'Activa' : 'Inactiva'}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(promo)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(promo.promotion_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPromo ? 'Editar Promoción' : 'Nueva Promoción'}
            </DialogTitle>
            <DialogDescription>
              Las promociones se aplican automáticamente cuando se cumplen las condiciones
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                placeholder="Ej: Happy Hour Cerveza"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                placeholder="Descripción opcional..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            {/* Discount Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de descuento</Label>
                <Select 
                  value={form.discount_type} 
                  onValueChange={(v) => setForm({ ...form, discount_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {DISCOUNT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(form.discount_type === 'percentage' || form.discount_type === 'fixed') && (
                <div className="space-y-2">
                  <Label>Valor del descuento</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}
            </div>

            {/* Days */}
            <div className="space-y-2">
              <Label>Días de la semana (dejar vacío = todos)</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <Button
                    key={day.value}
                    type="button"
                    variant={form.days_of_week.includes(day.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora inicio</Label>
                <Input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                />
              </div>
            </div>

            {/* Categories */}
            <div className="space-y-2">
              <Label>Categorías (dejar vacío = todas)</Label>
              <Select 
                value={form.category_ids.length > 0 ? form.category_ids[0] : ''}
                onValueChange={(v) => {
                  if (v && !form.category_ids.includes(v)) {
                    setForm({ ...form, category_ids: [...form.category_ids, v] });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Agregar categoría..." />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.category_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.category_ids.map((id) => {
                    const cat = categories.find(c => c.category_id === id);
                    return cat ? (
                      <Badge key={id} variant="secondary" className="cursor-pointer" onClick={() => {
                        setForm({ ...form, category_ids: form.category_ids.filter(c => c !== id) });
                      }}>
                        {cat.name} ×
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            {/* Active & Priority */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
                <Label>Promoción activa</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label>Prioridad:</Label>
                <Input
                  type="number"
                  className="w-20"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingPromo ? 'Guardar cambios' : 'Crear promoción'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
